import { first } from "lodash"
import config from "react-global-configuration"

import { PATH } from "containers/Survey/models"
import { getDermatoSubReasonValue } from "containers/Survey/dermato_models"
import { MAIN_PATIENT_ID } from "containers/Upload/services/constants"
import { Answer, Question, Value } from "containers/Survey/types"
import { AvailableDoctor, DoctorType, TreatingDoctorStatus } from "types/entity"
import { CallRequest } from "types/payload"

const BMI_RANGE = { min: 18.5, max: 25 }

const isHeightQuestion = (question?: Question) => question?.replies?.[0]?.unit === "m"

const toNumber = (value?: Value) => {
  const parsed = parseFloat(String(value ?? "").replace(",", "."))
  return Number.isFinite(parsed) ? parsed : undefined
}

const complyPreventionAnswers = (answers: Answer[], questions: Question[]) => {
  const filteredAnswers = answers.filter((b) =>
    /prevention-report-[0-9]{1,}/.test(b.questionId),
  )
  const filteredQuestions = questions.filter((q) =>
    /prevention-report-[0-9]{1,}/.test(q.id),
  )
  const getParentQuestion = (questionId: string) =>
    filteredQuestions.find(
      (q) => q.id === questionId || q.subquestion?.id === questionId,
  )
  const getCurrentQuestion = (questionId: string) => {
    const parent = getParentQuestion(questionId)
    if (!parent) return undefined
    return parent.id === questionId ? parent : parent.subquestion
  }

  const prevention = filteredAnswers.map((b) => {
    const parent = getParentQuestion(b.questionId)
    const question = getCurrentQuestion(b.questionId)
    return {
      sectionId: parent?.sectionId,
      sectionLabel: parent?.subtitle,
      id: String(question?.sourceId ?? ""),
      label: b.label ?? question?.label,
      values:
        question?.ui?.view === "code"
          ? b.value
          : b.value.map(
              (v) => question?.replies.find((r) => r.value === v)?.title,
            ),
      alert: !!question?.replies.some(
        (r) => r.preventionAlert && b.value.includes(r.value),
      ),
      pdf: {
        type: question?.pdf?.type,
        values: b.value.map(String),
      },
    }
  })

  filteredAnswers.forEach((b, index) => {
    if (getCurrentQuestion(b.questionId)?.preventionAlertRule !== "bmi") return
    const sectionId = getParentQuestion(b.questionId)?.sectionId
    const heightIndex = filteredAnswers.findIndex(
      (h) =>
        isHeightQuestion(getCurrentQuestion(h.questionId)) &&
        getParentQuestion(h.questionId)?.sectionId === sectionId,
    )
    const weight = toNumber(b.value[0])
    const height = heightIndex >= 0 ? toNumber(filteredAnswers[heightIndex].value[0]) : undefined
    if (!weight || !height) return
    const heightMeters = height / 100
    const bmi = weight / (heightMeters * heightMeters)
    if (bmi < BMI_RANGE.min || bmi > BMI_RANGE.max) {
      prevention[index].alert = true
      prevention[heightIndex].alert = true
    }
  })
  return prevention
}

export const buildSurveySubmitPayload = (
  answers: Answer[],
  questions: Question[],
  extras?: { dermatoPatientDetails?: string | null },
): CallRequest => {
  const complyResponse = (wantedId: string): Value | undefined => {
    const responses: Value[] | undefined =
      answers.find(({ questionId }) => questionId === wantedId)?.value ||
      undefined

    if (!responses?.length) return undefined

    if (responses.length === 1) {
      return first(responses)
    }

    const allPrimitive = responses.every((v) => typeof v !== "object")
    if (allPrimitive) {
      return responses.join(", ")
    }

    return responses as unknown as Value
  }

  const complyQuestionaryResponses = (wantedId: string): Value | undefined => {
    const responses: Value[] | undefined =
      answers.find(({ questionId }) => questionId === wantedId)?.value ||
      undefined
    if (!responses) return {}
    return responses.reduce(
      (result: { [key: string]: unknown }, { title, value }) => {
        result[title] = value
        return result
      },
      {},
    )
  }

  const uploadDocumentResponse = (wantedId: string): Value | undefined => {
    const responses: Value[] | undefined =
      answers.find(({ questionId }) => questionId === wantedId)?.value ||
      undefined
    if (!responses) return []
    return responses
  }

  const getDoctorChoice = () => {
    const doctors = answers.find(
      ({ questionId }) => questionId === PATH.choice_of_doctor,
    )?.value as AvailableDoctor[] | undefined
    const doctor = first(doctors) as AvailableDoctor | undefined

    const doctor_available_response = complyResponse(PATH.doctor_available)
    if (!doctor) {
      const hasDoctor =
        doctor_available_response === TreatingDoctorStatus.DOCTOR_UNAVAILABLE ||
        doctor_available_response ===
          TreatingDoctorStatus.DOCTOR_AWAY_CONSENT ||
        doctor_available_response === TreatingDoctorStatus.DOCTOR_AWAY_NO_CONSENT

      return {
        doctor: hasDoctor,
        doctor_available: false,
        treating_doctor_status:
          doctor_available_response as TreatingDoctorStatus,
      }
    }

    const exemptedByLabourDoctor =
      doctor.category_id === DoctorType.LABOUR_DOCTOR && { exemption: true }

    return {
      doctor_id: doctor.id,
      clinic_id: doctor.clinic_id ? doctor.clinic_id : doctor.clinic.id,
      doctor: true,
      doctor_available: true,
      ...exemptedByLabourDoctor,
    }
  }

  const payloadForDoctorType = (doctorType?: DoctorType) => {
    const anamnesis_questions = complyQuestionaryResponses(
      PATH.anamnesis_questions,
    ) as Record<string, unknown>
    if (doctorType === DoctorType.DERMATOLOGY) {
      const dermato_questions = complyQuestionaryResponses(
        PATH.dermato_questions,
      ) as Record<string, unknown>
      const dermato_anamnesis_questions = complyQuestionaryResponses(
        PATH.dermato_anamnesis_questions,
      ) as Record<string, unknown>
      return {
        medical: {
          tle_request_type: DoctorType.DERMATOLOGY,
          reason: complyResponse(PATH.dermato_reason),
          reason_details: getDermatoSubReasonValue(answers),
          ...anamnesis_questions,
          illness_details: anamnesis_questions?.[PATH.illness]
            ? complyResponse(PATH.illness)
            : undefined,
          allergies_details: anamnesis_questions?.[PATH.allergy]
            ? complyResponse(PATH.allergy)
            : undefined,
          dermatology: {
            body_zone: complyResponse(PATH.dermato_body_zone),
            other_symptoms: complyResponse(PATH.dermato_symptoms),
            phototype: complyResponse(PATH.dermato_phototype),
            ...dermato_questions,
            ...dermato_anamnesis_questions,
            ...(extras?.dermatoPatientDetails
              ? { dermato_patient_details: extras.dermatoPatientDetails }
              : {}),
            dermato_problem_duration: complyResponse(
              PATH.dermato_problem_duration,
            ),
            dermato_zone_evolution_duration: dermato_anamnesis_questions?.[
              PATH.dermato_zone_evolution
            ]
              ? complyResponse(PATH.dermato_zone_evolution_duration)
              : undefined,
            dermato_history_details: dermato_anamnesis_questions?.[
              PATH.dermato_history
            ]
              ? complyResponse(PATH.dermato_history)
              : undefined,
          },
          pharmacist: complyResponse(PATH.tle_pharmacist) as { rpps?: string; finess?: string; name?: string } | undefined,
        },
      }
    }
    if (doctorType === DoctorType.PHARMACIST) {
      return {
        medical: {
          prevention: complyPreventionAnswers(answers, questions),
        },
      }
    }
    return {
      medical: {
        ...anamnesis_questions,
        illness_details: anamnesis_questions?.[PATH.illness]
          ? complyResponse(PATH.illness)
          : undefined,
        allergies_details: anamnesis_questions?.[PATH.allergy]
          ? complyResponse(PATH.allergy)
          : undefined,
        pregnant_or_breastfeeding: complyResponse(PATH.pregnant),
        reason: complyResponse(PATH.reason),
      },
    }
  }

  const doctorType = complyResponse(PATH.doctor_type) as DoctorType
  const selectedPatientId = complyResponse(PATH.patient)
  return {
    ...config.get("mock.call"),
    relative_id:
      selectedPatientId !== MAIN_PATIENT_ID ? selectedPatientId : undefined,
    vitals: answers.find(({ questionId }) => questionId === PATH.vitals)?.value.filter((v) => typeof v === "string"),
    ...payloadForDoctorType(doctorType),
    documents: uploadDocumentResponse(
      doctorType !== DoctorType.PHARMACIST
        ? PATH.selected_documents
        : PATH.selected_documents_prevention,
    ),
    payment_method_id: complyResponse("payment_method_id"),
    payment_intent_id: complyResponse("payment_intent_id"),
    payment_sheet_intent_id: complyResponse("payment_sheet_intent_id"),
    exemption: complyResponse(PATH.exemption) ?? false,
    share_personal_data: complyResponse(PATH.consent) ?? true,
    carer_guid: complyResponse(PATH.selected_carer),
    ...getDoctorChoice(),
  }
}
